import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { checkAvailabilityHold, createActivityBooking, getUserWallet } from '@share/store/slices';
import { ICheckoutPayload, IGuestPayload, IHolderPayload, ICardPayload } from '@share/common-types';
import { Button, Typography, Row, Col, Card, Checkbox, Select, Input, List, Skeleton, Spin, Modal } from 'antd';
import { ArrowLeftOutlined, CheckCircleFilled, ClockCircleOutlined, SafetyOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { BookingSummary } from '../booking-summary';
import { PaymentForm, PaymentFormData } from '@share/components';
import { navigateTo, getAccountNumberPath, getAccountUsernameFromPath, insertSpacesInPhone, GetHomeParams, RootState, AppDispatch } from '@share/utils';
import { detectCardType } from '@utils';
import { countriesCode, Languages, DEFAULT_RESIDENCY, Routes, US_STATES, PRE_POPULATE_GUEST, CROSS_SELL_AT_CHECKOUT, getCheckoutHolderInitialState, getCheckoutCardInitialState, guestPrePopulatedName } from '@share/constants';
import { IActivityAgeBand, IAgeRange, IPickupDetails, LanguageGuide, Question, QuestionAgesExclude, QuestionFirstNamesExclude, QuestionGroupTraveler, QuestionLastNamesExclude, StartingPoint, getGuestTypeForAge, isTravelerAgeBandQuestion } from '@common-types';
import { initTravelerAnswers } from '@share/store/slices';
import { useCheckoutValidation, getAgeBandAnswerCodeForQuestion } from '@share/schema-validation';
import { GuestQuestions, BookingQuestions, OfferExpiredModal, CrossSellSection } from '@components';
import { Link, useLocation } from "react-router-dom";
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from '@share/utils';
import { ActivityExpressCheckoutButtons } from '../ActivityExpressCheckoutButtons/component';
import { ActivityForceFullStripeCheckout } from '../ActivityForceFullStripeCheckout/component';
import { buildBillingAddressPayload, computeIsStripePaymentMethodsEnabled } from '@share/utils';
import { PaymentMethodsSelect } from '../ActivityExpressCheckoutButtons/payment-methods-select';
import './styles.scss';
import { isEmpty } from 'lodash';
import moment from 'moment';


const { Title, Text } = Typography
const { Option } = Select;

export interface MediaImage {
    mimeType: string;
    language: string;
    dpi: number;
    height: number;
    width: number;
    resource: string;
    sizeType: string;
}

export interface DetailLabel {
    sessionId: string;
    rateKey: string;
    activityCode: string;
    price: number;
    /** Pre-tax base price (price − taxes). Basis for the wallet/points discount cap. */
    priceWithoutTaxes?: number;
    taxes?: number;
    savings: number;
    pubPrice: number;
    marginatorPrice: number;
    marginatorSavings: number;
    marginatorsavePercents: number;
    name: string;
    mediaImages: MediaImage[];
    refundable: boolean;
    time: string;
    duration: string;
    currency: string;
    review: number;
    rating: number;
    questions: Question[];
    languageGuides: LanguageGuide[];
    startingPoints?: StartingPoint[];
    travelerPickups?: IPickupDetails[];
    allowCustomTravelerPickup?: boolean;
    maxWalletClientCash?: number;
    maxRewardWalletAmount?: number;
    isEligibleForRewardWallet?: boolean;
    cancellationPolicies?: any[];
    cancellationPolicyText?: string;
    ageRange?: IAgeRange;
    ageBands?: IActivityAgeBand[];
}

export interface ActivityDates {
    startDate: string;
}

export interface ActivityCheckoutProps {
    detailLabel: DetailLabel;
    dates: ActivityDates;
}

interface IMultiplePayments {
  amazonPay: boolean;
  applePay: boolean;
  googlePay: boolean;
  link: boolean;
  paypal: boolean;
}


export const ActivityCheckout: React.FC<ActivityCheckoutProps> = ({ detailLabel, dates }) => {
    const activityDateIsoForAge =
        dates?.startDate && moment(dates.startDate, 'YYYY-MM-DD', true).isValid()
            ? dates.startDate
            : moment().format('YYYY-MM-DD');
    const intl = useIntl();

    const formatGuestTypeWithAge = (type: string | undefined, age: number | string | null | undefined): string | null => {
        const t = type?.toUpperCase();
        let typeLabel: string | null = null;
        if (t === 'ADULT') {
            typeLabel = intl.formatMessage({ id: 'checkout.guestTypeAdult', defaultMessage: 'Adult' });
        } else if (t === 'CHILD') {
            typeLabel = intl.formatMessage({ id: 'checkout.guestTypeChild', defaultMessage: 'Child' });
        } else if (t === 'INFANT') {
            typeLabel = intl.formatMessage({ id: 'checkout.infant', defaultMessage: 'Infant' });
        } else if (t === 'SENIOR') {
            typeLabel = intl.formatMessage({ id: 'checkout.guestTypeSenior', defaultMessage: 'Senior' });
        } else if (t === 'YOUTH') {
            typeLabel = intl.formatMessage({ id: 'checkout.guestTypeYouth', defaultMessage: 'Youth' });
        } else if (t === 'TRAVELER') {
            typeLabel = intl.formatMessage({ id: 'checkout.guestTypeTraveler', defaultMessage: 'Traveler' });
        } else if (t) {
            typeLabel = t.charAt(0) + t.slice(1).toLowerCase();
        }
        if (typeLabel == null) {
            return null;
        }
        const n = Number(age);
        if (age === '' || age == null || !Number.isFinite(n) || n < 0) {
            return typeLabel;
        }
        return intl.formatMessage(
            {
                id: 'checkout.guestTypeWithAge',
                defaultMessage: '{type} - {age, plural, one {# year old} other {# years old}}',
            },
            { type: typeLabel, age: n }
        );
    };

    const dispatch = useDispatch<AppDispatch>();

    const location = useLocation();

    const { roomsSelected } = useSelector((state: RootState) => state.roomsStore);
    const { loading: isBookingLoading, bookingAnswers, travelerAnswers, rewardId, selectedActivityReviewClientCash } = useSelector((state: RootState) => state.activityCheckoutStore);
    const { conditionalData, questionData } = useSelector((state: RootState) => state.activityQuestionStore);
    const { data, hold, selectedDate } = useSelector((state: RootState) => state.activityAvailabilityStore);
    const { user, account, userWallet } = useSelector((state: RootState) => state.loginStore);
    const navigationMenuStore = useSelector((state: RootState) => state.navigationMenuStore);

    const [paymentSelected, setPaymentSelected] = useState<string>('');
    const [scrollToPaymentSelect, setScrollToPaymentSelect] = useState<boolean>(false);
    const [expressWalletProbeComplete, setExpressWalletProbeComplete] = useState(false);
    const [hasExpressWalletMethods, setHasExpressWalletMethods] = useState(false);

    const stripeRegionOk = computeIsStripePaymentMethodsEnabled(
        account,
        navigationMenuStore?.items?.siteId,
        navigationMenuStore?.items?.organizationId,
    );
    const suppressStripePaymentUI = stripeRegionOk && !!account?.forceStripeFull;
    const showExpressCheckoutFlow = !suppressStripePaymentUI && stripeRegionOk && expressWalletProbeComplete && hasExpressWalletMethods;

    const buttonsPreferences = account?.preferences?.filter(
        (x: any) => x.key === 'payment_buttons',
    );
    const prefButtonsRaw = buttonsPreferences?.[0]?.value;
    const paymentButtonKeysFromPrefs = prefButtonsRaw
        ? String(prefButtonsRaw)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
    const paymentButtonKeys =
        account?.paymentButtons && account.paymentButtons.length > 0
            ? account.paymentButtons
            : paymentButtonKeysFromPrefs;
    // Effective payment method (mirrors front-rsi). When Stripe is enabled we defer to 'card' only
    // once the express wallet probe has completed and found no wallets — otherwise the probe is still
    // running (show the skeleton, paymentEffective '') or wallets exist (defer to the picker choice).
    const paymentEffective = !stripeRegionOk
        ? 'card'
        : !expressWalletProbeComplete
          ? ''
          : showExpressCheckoutFlow
            ? paymentSelected
            : 'card';
    const isCardPayment = !suppressStripePaymentUI && paymentEffective === 'card';
    const shouldMountStripeExpressElements = !suppressStripePaymentUI && stripeRegionOk && (!expressWalletProbeComplete || hasExpressWalletMethods);
    const expressPaymentSelected = paymentSelected === 'card' ? '' : paymentSelected;

    const accountName = getAccountUsernameFromPath(location);

    const [remainingTimeLabel, setRemainingTimeLabel] = useState<string | null>(null);
    const [showExpiredModal, setShowExpiredModal] = useState(false);

    const [currentStep, setCurrentStep] = useState(1);

    const [holder, setHolder] = useState<IHolderPayload>(() => getCheckoutHolderInitialState());

    const [paxes, setPaxes] = useState<IGuestPayload[]>([]);
    const [card, setCard] = useState<ICardPayload>(() => getCheckoutCardInitialState());

    const [termsAccepted, setTermsAccepted] = useState(false);
    const [cancellationAccepted, setCancellationAccepted] = useState(false);
    const [clientCashNotRefundableAccepted, setClientCashNotRefundableAccepted] = useState(false);
    const [showNotRefundableModal, setShowNotRefundableModal] = useState(false);

    // Applying wallet client cash turns the booking non-refundable (mirrors hotels/cars). When it
    // applies, the user must acknowledge the change before booking. A wallet profile with
    // SkipNonRefundable opts out: the rate keeps the provider's real refundability and policies.
    const usingClientCashNonRefundable =
        !account?.walletSkipNonRefundable &&
        !account?.walletIsExternal &&
        !!account?.hasClientCash &&
        !!userWallet &&
        (selectedActivityReviewClientCash ?? 0) > 0;
    // Local (not persisted) wallet-points validity; resets on remount when navigating back to checkout.
    const [isReviewClientCashValid, setIsReviewClientCashValid] = useState(true);
    const [selectedLanguageGuide, setSelectedLanguageGuide] = useState<LanguageGuide | null>(
        detailLabel.languageGuides && detailLabel.languageGuides.length > 0 ? detailLabel.languageGuides[0] : null
    );

   const [multipayment, setMultipayement] =useState<IMultiplePayments>({
        amazonPay: false,
        applePay: false,
        googlePay: false,
        link: false,
        paypal: false,
    });

    const {
        errors,
        setErrors,
        validateHolder,
        validatePaxes,
        handleHolderChange,
        handlePaxChange,
        isStep1Valid,
        isStep2Valid,
        isStep3Valid
    } = useCheckoutValidation(
        holder,
        setHolder,
        paxes,
        setPaxes,
        card,
        termsAccepted,
        cancellationAccepted,
        detailLabel,
        bookingAnswers,
        travelerAnswers,
        selectedLanguageGuide,
        conditionalData,
        questionData,
        dates?.startDate
    );

    const getLanguageName = (code: string) => {
        const lang = Languages.find(l => l.code.toLowerCase() === code.toLowerCase());
        if (lang) {
            const id = `languages.${lang.name.toLowerCase()}`;
            return intl.formatMessage({ id, defaultMessage: lang.name });
        }
        return code.charAt(0).toUpperCase() + code.slice(1);
    };

    const phoneDisplay = useMemo(() => {
        const digits = (holder.phone || '').replace(/\D/g, '');
        const parts = insertSpacesInPhone(digits);
        return parts ? parts.join('-') : digits;
    }, [holder.phone]);

    /** Stable props + inferred card type so React state matches API payload (mirrors RSI behavior). */
    const paymentFormSyncProps = useMemo((): Partial<PaymentFormData> => {
        const digits = (card.cardNumber || '').replace(/\D/g, '');
        const fromPan = digits ? detectCardType(digits) : undefined;
        const effectiveCardType =
            card.cardType !== undefined &&
            card.cardType !== null &&
            String(card.cardType).trim() !== ''
                ? String(card.cardType).trim()
                : fromPan != null
                  ? String(fromPan)
                  : '';
        const exp = card.expireDate || '';
        return {
            cardNumber: card.cardNumber,
            nameOnCard: card.holderName,
            expiryMonth: exp.length >= 2 ? exp.substring(0, 2) : '',
            expiryYear: exp.length > 2 ? exp.substring(2) : '',
            securityCode: card.cvv,
            cardType: effectiveCardType,
        };
    }, [card.cardNumber, card.holderName, card.expireDate, card.cvv, card.cardType]);

    const isValidDate = (date?: string) =>
        Boolean(date && !date.startsWith("0001"));

    const formatDate = (date: string) => {
        if (!date) return date ?? '';
        const parsed = moment(date.split('T')[0], 'YYYY-MM-DD', true);
        return parsed.isValid() ? parsed.format('MMMM D, YYYY') : date;
    };

    useEffect(() => {
        if (user && detailLabel.rateKey) {
            //const hold = localStorage.getItem(`activityAvailabilityHold_${detailLabel.rateKey}`);
            if (!hold) {
                dispatch(checkAvailabilityHold({
                    activityCode: detailLabel.activityCode,
                    startTime: data?.modalities?.[0]?.rates?.[0]?.sessions?.[0]?.code,
                    rateKey: detailLabel.rateKey,
                    onSuccess: () => { },
                    onFailure: () => {
                        setShowExpiredModal(true);
                    },
                }));

            }
        }
    }, [user, detailLabel?.rateKey]);

    useEffect(() => {
        if (roomsSelected) {
            const initialPaxes: IGuestPayload[] = [];
            roomsSelected.forEach(room => {
                for (let i = 0; i < room.adultsCount; i++) {
                    const age = room.adultAges?.[i] || 18;
                    initialPaxes.push({
                        givenName: '',
                        surName: '',
                        age,
                        type: getGuestTypeForAge(age, detailLabel?.ageBands, 'ADULT'),
                    });
                }
                room.kids?.forEach(kid => {
                    const age = kid.age || 0;
                    initialPaxes.push({
                        givenName: '',
                        surName: '',
                        age,
                        type: getGuestTypeForAge(age, detailLabel?.ageBands, 'CHILD'),
                    });
                });
            });
            if (PRE_POPULATE_GUEST) {
                initialPaxes.forEach((_, idx) => {
                    const token = guestPrePopulatedName(idx);
                    initialPaxes[idx] = {
                        ...initialPaxes[idx],
                        givenName: token,
                        surName: token,
                    };
                });
            }
            setPaxes(initialPaxes);
            setErrors((prev: any) => ({ ...prev, paxes: initialPaxes.map(() => ({})) }));
            dispatch(initTravelerAnswers(initialPaxes.length));
        }
    }, [roomsSelected, dispatch, dates?.startDate]);

    const ageBandsKey = useMemo(
        () => (detailLabel?.ageBands?.length ? JSON.stringify(detailLabel.ageBands) : ''),
        [detailLabel?.ageBands]
    );

    useEffect(() => {
        if (!ageBandsKey) {
            return;
        }
        const bands = detailLabel?.ageBands;
        if (!bands?.length) {
            return;
        }
        setPaxes((prev) => {
            if (prev.length === 0) {
                return prev;
            }
            return prev.map((p) => ({
                ...p,
                type: getGuestTypeForAge(p.age, bands, p.type),
            }));
        });
    }, [ageBandsKey, detailLabel?.ageBands]);

    const effectiveHold = useMemo(() => {
        if (hold) {
            return hold;
        }

        /*
        try {
            const raw = detailLabel?.rateKey
                ? null //localStorage.getItem(`activityAvailabilityHold_${detailLabel.rateKey}`)
                : null;
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
        */

        return null;
    }, [hold, detailLabel?.rateKey]);

    useEffect(() => {
        const expiresAt = effectiveHold?.availabilityExpiresAt;
        if (!expiresAt) return;

        const expiresMs = new Date(expiresAt).getTime();
        if (Number.isNaN(expiresMs)) return;
        if (expiresMs <= Date.now()) {
            setShowExpiredModal(true);
            return;
        }

        const updateRemaining = () => {
            const now = Date.now();
            const diffMs = Math.max(0, expiresMs - now);
            if (diffMs <= 0) {
                setRemainingTimeLabel(null);
                setShowExpiredModal(true);
                return;
            }
            const totalSeconds = Math.floor(diffMs / 1000);
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            setRemainingTimeLabel(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        };

        updateRemaining();

        const intervalId = setInterval(updateRemaining, 1000);
        const timeoutId = setTimeout(() => {
            setRemainingTimeLabel(null);
            setShowExpiredModal(true);
        }, expiresMs - Date.now());

        return () => {
            clearInterval(intervalId);
            clearTimeout(timeoutId);
        };
    }, [effectiveHold?.availabilityExpiresAt]);

    const handleNextStep = () => {
        if (currentStep === 1) {
            if (validateHolder()) {
                const newPaxes = [...paxes];
                if (newPaxes.length > 0) {
                    newPaxes[0] = {
                        ...newPaxes[0],
                        givenName: holder.givenName,
                        surName: holder.surName
                    };
                    setPaxes(newPaxes);
                }
                setCurrentStep(2);
            }
        } else if (currentStep === 2) {
            if (validatePaxes()) {
                setCard(prev => ({
                    ...prev,
                    addressLine: holder.addressLine,
                    city: holder.city,
                    state: holder.stateCode,
                    country: holder.country,
                    countryCode: holder.countryCode,
                    zipCode: holder.postalCode,
                    phone: holder.phone,
                    holderName: `${holder.givenName} ${holder.surName}`
                }));
                setCurrentStep(3);
            }
        }
    };

    const convertToBookingQuestionAnswers = (answers: any) => {
        if (!answers) return [];

        return Object.entries(answers)
            .filter(([_, data]: any) => data?.value !== undefined && data?.value !== '')
            .map(([questionId, data]: any) => {
                const question = detailLabel.questions?.find(q => q.code === questionId);
                const unit = data.unit || (question?.units && question.units.length > 0 ? question.units[0] : undefined);
                return {
                    questionId,
                    answer: data.value,
                    ...(unit && { unit })
                };
            });
    };

    const convertToTravelerQuestionAnswers = (travelerAnswers: any[]) => {
        const autoFilledAnswers = detailLabel.questions?.filter((q: any) => {
            const label = q.code || q.text?.toUpperCase() || '';
            const hasAutoFilledAnswer =
                QuestionFirstNamesExclude.includes(label) ||
                QuestionLastNamesExclude.includes(label) ||
                QuestionAgesExclude.includes(label) ||
                isTravelerAgeBandQuestion(q);
            return QuestionGroupTraveler.includes(q.group) && hasAutoFilledAnswer;
        });

        if (!autoFilledAnswers?.length && (!travelerAnswers || !Array.isArray(travelerAnswers))) {
            return [];
        }

        const travelerAnswersData = travelerAnswers.reduce((acc, traveler, index) => {
            const guest = paxes[index];
            const baseAnswers = Object.entries(traveler)
                .filter(([_, data]: any) => data?.value !== undefined && data?.value !== '')
                .map(([questionId, data]: any) => {
                    const question = detailLabel.questions?.find(q => q.code === questionId);
                    const unit = data.unit || (question?.units && question.units.length > 0 ? question.units[0] : undefined);
                    return {
                        questionId,
                        answer: data.value,
                        ...(unit && { unit })
                    }
                });
            const autoFilledAnswers: any[] = [];
            detailLabel.questions?.forEach((q: any) => {
                if (QuestionGroupTraveler.includes(q.group)) {
                    const label = q.code || q.text?.toUpperCase() || '';
                    const hasAnswer = baseAnswers.some(a => a.questionId === q.code);

                    if (!hasAnswer) {
                        const unit = q.units && q.units.length > 0 ? q.units[0] : undefined;
                        if (QuestionFirstNamesExclude.includes(label) && guest?.givenName) {
                            autoFilledAnswers.push({
                                questionId: q.code,
                                answer: guest.givenName,
                                ...(unit && { unit })
                            });
                        } else if (QuestionLastNamesExclude.includes(label) && guest?.surName) {
                            autoFilledAnswers.push({
                                questionId: q.code,
                                answer: guest.surName,
                                ...(unit && { unit })
                            });
                        } else if (QuestionAgesExclude.includes(label)) {
                            const rawAge = (guest as { age?: number | string | null })?.age;
                            if (rawAge !== undefined && rawAge !== null && rawAge !== '') {
                                autoFilledAnswers.push({
                                    questionId: q.code,
                                    answer: String(rawAge),
                                    ...(unit && { unit })
                                });
                            }
                        } else if (isTravelerAgeBandQuestion(q)) {
                            const bandCode = getAgeBandAnswerCodeForQuestion(q, guest, detailLabel.ageBands);
                            if (bandCode) {
                                autoFilledAnswers.push({
                                    questionId: q.code,
                                    answer: bandCode,
                                    ...(unit && { unit })
                                });
                            }
                        }
                    }
                }
            });

            const allAnswers = [...baseAnswers, ...autoFilledAnswers];

            if (allAnswers.length > 0) {
                acc.push({
                    travelerIndex: index,
                    answers: allAnswers
                });
            }

            return acc;
        }, [] as any[]);

        return travelerAnswersData;
    };

    const { userLanguage, lifeStyle } = useSelector((state: RootState) => state.loginStore);
    const { selected: selectedCurrency } = useSelector((state: RootState) => state.currencyStore);

    const handleCompleteBooking = async (isCheckoutExpress = false, stripe = null, elements = null, paymentDescription = '', stripePaymentMethodType = ''): Promise<boolean> => {
        if (!termsAccepted || !cancellationAccepted || !isReviewClientCashValid || (usingClientCashNonRefundable && !clientCashNotRefundableAccepted)) {
            return false;
        }
        console.log("check in handleCompleteBooking",isCheckoutExpress );
        const isCheckoutExpressFlag = typeof isCheckoutExpress === 'boolean' ? isCheckoutExpress : false;
        // `confirmPayment` is Stripe.js's own method - not our `confirmActivityPayment`
        // thunk. The merge's symbol rename caught this probe too, so it never matched,
        // `stripe` was always passed as null and the PaymentIntent was never confirmed
        // (TRAV23-5160).
        const stripeFromArgs =
            stripe && typeof (stripe as any).confirmPayment === 'function'
                ? stripe
                : (isCheckoutExpress as any) && typeof (isCheckoutExpress as any).confirmPayment === 'function'
                  ? (isCheckoutExpress as any)
                  : null;
        const elementsFromArgs =
            elements && typeof (elements as any).submit === 'function'
                ? elements
                : stripe && typeof (stripe as any).submit === 'function'
                  ? stripe
                  : null;

        const dialCode = countriesCode.find(c => c.isoCode === holder.countryCode)?.dialCode || '';
        const cleanDialCode = dialCode.startsWith('+') ? dialCode : `+${dialCode}`;
        const finalPhone = `${cleanDialCode}${holder.phone}`;

        const cardDigits = (card.cardNumber || '').replace(/\D/g, '');
        const existingType =
            card.cardType !== undefined && card.cardType !== null && String(card.cardType).trim() !== ''
                ? String(card.cardType).trim()
                : '';
        const inferredType = cardDigits ? detectCardType(cardDigits) : undefined;
        const cardTypeForBooking = existingType || (inferredType != null ? String(inferredType) : '');

        const billingAddress = buildBillingAddressPayload({
            holderName: [holder.givenName, holder.surName].filter(Boolean).join(' '),
            addressLine: holder.addressLine,
            city: holder.city,
            state: holder.stateCode,
            postalCode: holder.postalCode,
            // ISO codes on both, as the `card` object carried them — the payload's `holder.country`
            // is swapped for the display name further down, which the BFF does not want here.
            country: holder.country || holder.countryCode,
            countryCode: holder.countryCode || holder.country,
            phone: finalPhone,
        });

        const payload: ICheckoutPayload = {
            sessionId: detailLabel.sessionId,
            rateKey: detailLabel.rateKey,
            residency: holder.countryCode || DEFAULT_RESIDENCY,
            language: userLanguage || "en",
            currency: selectedCurrency || detailLabel.currency || "USD",
            holder: {
                ...holder,
                phone: finalPhone,
                country: countriesCode.find(c => c.isoCode === holder.countryCode)?.name || holder.country
            },
            paxes: paxes.map(({ birthDate: _bd, ...pax }) => pax),
            ...(isCheckoutExpressFlag
                // The PaymentElement is pinned to one `payment_method_types` token, so the BFF intent has to
                // declare the same one or Stripe rejects the confirm. `cardOnly` is the existing boolean the
                // BFF already understands (→ `payment_method_types:['card']`) and is unchanged for the card
                // path; `stripePaymentMethodType` carries the method for the Klarna / Afterpay / Cash App Pay
                // rows. The wallet path (ExpressCheckoutElement) sends neither and stays on automatic methods.
                // 
                // Force-full CARD path (PaymentElement) pins the client Elements to `payment_method_types:['card']`.
                // Tell the BFF to create the PaymentIntent card-only too — otherwise it defaults to automatic
                // payment methods (card + amazon_pay) and Stripe rejects the confirm with a method-types mismatch.
                // Wallets (Amazon Pay / etc.) must stay on automatic methods, so only flag when paying by card.
                // Mirrors the hotels (rsi) `cardOnly` fix.
                //
                // No `card` goes out here, and `card` was the BFF's only source for the traveler's
                // address — so send it as `billingAddress` instead. Unlike hotels (where the address
                // lived in the credit-card form and had to be rebuilt as an address-only form), the
                // activities wizard already collects it in step 1, so it comes straight off `holder`.
                ? {
                    isCheckoutExpress: true,
                    ...(paymentDescription === 'Card' ? { cardOnly: true } : {}),
                    ...(stripePaymentMethodType ? { stripePaymentMethodType } : {}),
                    ...(billingAddress ? { billingAddress } : {}),
                  }
                : {
                    card: {
                        ...card,
                        phone: finalPhone,
                        cardType: cardTypeForBooking,
                    },
                }),
            languageGuide: selectedLanguageGuide,
            bookingQuestionAnswers: convertToBookingQuestionAnswers(bookingAnswers),
            travelerQuestionAnswers: convertToTravelerQuestionAnswers(travelerAnswers),
            ...(rewardId != null ? { rewardId } : {}),
            ...(selectedActivityReviewClientCash != null && selectedActivityReviewClientCash > 0 ? { clientCashAmount: selectedActivityReviewClientCash } : {}),
            ...(selectedDate ? { serviceFrom: selectedDate, serviceTo: selectedDate } : {}),
            lifeType: lifeStyle ?? 'None',
        };
        const resultAction = await dispatch(
            createActivityBooking({ payload, stripe: stripeFromArgs as any, elements: elementsFromArgs as any, paymentDescription })
        );
        if (
            createActivityBooking.fulfilled.match(resultAction) &&
            ['confirmed', 'pending'].includes(String((resultAction as any)?.payload?.bookingStatus || '').toLowerCase())
        ) {
            // Booking succeeded — the wallet balance was just consumed. Refresh it from the BFF so the
            // header/wallet UI reflects the new balance. `account.hasClientCash` is already normalized for
            // both RSI (forced off when no matching/default profile) and non-RSI accounts, so this one
            // check covers both. Only fires when a wallet user is loaded.
            const walletUser = userWallet ?? user;
            if (account?.hasClientCash && walletUser) {
                dispatch(getUserWallet(walletUser));
            }

            const accountPath = getAccountNumberPath(window.location as any);
            navigateTo(`${accountPath}${Routes.ActivityConfirmation}/${detailLabel.activityCode}/${detailLabel.rateKey}${GetHomeParams(account)}`);
            return true;
        }
        return false;
    };

    const cancellationPolicies = detailLabel?.cancellationPolicies;
    // Same rule as the acknowledgement checkbox: once points force the booking non-refundable
    // the provider's real policies no longer apply, so show the non-refundable text instead of
    // the rate's own (refundable) prose, and hide its policy list.
    const cancellationPolicyText = usingClientCashNonRefundable
        ? intl.formatMessage({ id: 'cancellation.not_refundable.message.policy', defaultMessage: 'In the event of no-show, your booking will not qualify for a refund.\n There are no cancellation or modifications allowed for this reservation.' })
        : (detailLabel?.cancellationPolicyText || intl.formatMessage({ id: 'cancellation.not_refundable.message.policy', defaultMessage: 'In the event of no-show, your booking will not qualify for a refund.\n There are no cancellation or modifications allowed for this reservation.' }));
    const refundability = detailLabel?.refundable && !usingClientCashNonRefundable;
    const isRefundableCancellationPolicy = (cancellationPolicies?.length ?? 0) > 0 && !!refundability;
    const isNonRefundableCancellationPolicy = !refundability && !isEmpty(cancellationPolicyText);
    const displayCancellationPolicy = isRefundableCancellationPolicy || isNonRefundableCancellationPolicy;

    // Terms + cancellation acknowledgements. Rendered inline (before the button) in the non-force-full
    // flow; in force-full it is passed as the `footer` of ActivityForceFullStripeCheckout so it sits
    // directly above "Complete booking" instead of scrolling off-screen above the payment picker.
    const acknowledgementsSection = (
        <>
            <div className="policy-block text-start">
                {displayCancellationPolicy && (
                    <>
                        <Title level={3}><FormattedMessage id="activity.details.cancellationPolicy" defaultMessage="Cancellation Policy" /></Title>

                        {isRefundableCancellationPolicy && (
                            <List
                                className="cancellation-policy-list"
                                dataSource={detailLabel?.cancellationPolicies || []}
                                renderItem={(policy) => {
                                    if (
                                        !policy?.from ||
                                        !isValidDate(policy.from)
                                    )
                                        return null;

                                    const amount = Number(policy.amount || 0);

                                    return (
                                        <List.Item>
                                            <SafetyOutlined className="policy-icon" />
                                            <Text>
                                                {amount === 0 ? (
                                                    <>
                                                        <FormattedMessage id="activity.details.freeCancellationUntil" defaultMessage="Free cancellation until" />{" "}
                                                        <strong>
                                                            {formatDate(policy.from)}
                                                        </strong>
                                                    </>
                                                ) : (
                                                    <FormattedMessage
                                                        id="activity.details.cancellationFeeApply"
                                                        defaultMessage="A fee of {amount} will apply for cancellations made on or after {date}."
                                                        values={{
                                                            amount: <strong>{policy.currency || detailLabel.currency} {amount.toFixed(2)}</strong>,
                                                            date: <strong>{formatDate(policy.from)}</strong>
                                                        }}
                                                    />
                                                )}
                                            </Text>
                                        </List.Item>
                                    );
                                }}
                            />)}

                        {isNonRefundableCancellationPolicy && (
                            <div className="cancellation-policy-text" style={{ marginTop: '10px' }}>
                                <SafetyOutlined className="policy-icon" style={{ color: 'rgb(0, 135, 104)', fontSize: '18px', marginRight: '12px' }} />
                                <Text>{cancellationPolicyText}</Text>
                            </div>)}

                    </>)}

                <Checkbox
                    checked={cancellationAccepted}
                    onChange={(e) => setCancellationAccepted(e.target.checked)}
                    className="policy-checkbox mt-3"
                >
                    <FormattedMessage id="checkout.cancellationAcknowledge" defaultMessage="I acknowledge that I have read and accepted the cancellation policy." />
                </Checkbox>
            </div>

            <div className="terms-block text-start">
                <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="terms-checkbox"
                >
                    <FormattedMessage id="checkout.privacyPolicyAcknowledge" defaultMessage="I agree to the Terms of Use and Privacy Policy." />
                </Checkbox>
            </div>

            {usingClientCashNonRefundable && (
                <div className="terms-block text-start">
                    <Checkbox
                        checked={clientCashNotRefundableAccepted}
                        onChange={(e) => {
                            setClientCashNotRefundableAccepted(e.target.checked);
                            setShowNotRefundableModal(e.target.checked);
                        }}
                        className="terms-checkbox"
                    >
                        <FormattedMessage
                            id="wallet.client.not_refundable.acknowledge"
                            values={{ clientCash: selectedActivityReviewClientCash, clientCashName: account?.walletClientCashName }}
                        />
                    </Checkbox>
                </div>
            )}
        </>
    );

    return (
        <div className="activity-checkout">
            {/* Page-level booking overlay: shown while a booking is in flight — including the Stripe
                express wallet resume (`book-express-resume`) which runs inside `createActivityBooking`, so
                `isBookingLoading` stays true throughout. Blocks every button on the page. */}
            {isBookingLoading && (
                <div className="activity-checkout__loading-overlay">
                    <Spin size="large" />
                </div>
            )}
            <div className='activity-checkout-container'>
                <div className="checkout-header">
                    <div className="back-button mb-0">
                        <ArrowLeftOutlined />
                        <Link to={`/${accountName}${Routes.ActivityDetail}${`/${detailLabel.activityCode}`}${location.search}`}>
                            <FormattedMessage id="checkout.backToActivityDetails" defaultMessage="Back to activity details" />
                        </Link>

                    </div>
                    <div className="text-start">
                        <Title level={2} className="page-title"><FormattedMessage id="checkout.reviewAndBook" defaultMessage="Review & Book" /></Title>
                        <Text className="page-subtitle"><FormattedMessage id="checkout.enterDetailsAndCompleteBooking" defaultMessage="Enter your details and complete your booking now." /></Text>
                    </div>
                </div>

                {remainingTimeLabel !== null && (
                    <div className="checkout-hold-timer">
                        <ClockCircleOutlined className="checkout-hold-timer__icon" />
                        <span>
                            <FormattedMessage
                                id="checkout.holdingYourSpot"
                                defaultMessage="Holding your spot for {time} minutes"
                                values={{ time: <strong>{remainingTimeLabel}</strong> }}
                            />
                        </span>
                    </div>
                )}

                <Row gutter={24}>
                    <Col xs={24} lg={14} xl={16}>
                        <div className="booking-steps">
                            {/* Step 1: Contact Detail */}
                            <Card className={`step-card ${currentStep === 1 ? 'active' : ''}`}>
                                <div className="step-header" onClick={() => setCurrentStep(1)}>
                                    <div className={`step-number ${currentStep > 1 ? 'completed' : ''}`}>
                                        {currentStep > 1 ? <CheckCircleFilled /> : 1}
                                    </div>
                                    <Title level={4} className="step-title"><FormattedMessage id="checkout.contactDetails" defaultMessage="Contact details" /></Title>
                                </div>
                                {currentStep === 1 && (
                                    <div className="step-content">
                                        <Text className="step-description"><FormattedMessage id="checkout.contactDescription" defaultMessage="We'll use this information to send you confirmation and updates about your booking" /></Text>

                                        <Row gutter={[16, 16]} className="mt-4">
                                            <Col span={4}>
                                                <div className="form-item">
                                                    <Text className="label"><FormattedMessage id="checkout.title" defaultMessage="Title" /></Text>
                                                    <Select
                                                        value={holder.namePrefix}
                                                        onChange={(val) => handleHolderChange('namePrefix', val)}
                                                        style={{ width: '100%' }}
                                                    >
                                                        <Option value="Mr">Mr</Option>
                                                        <Option value="Mrs">Mrs</Option>
                                                        <Option value="Ms">Ms</Option>
                                                        <Option value="Dr">Dr</Option>
                                                    </Select>
                                                </div>
                                            </Col>
                                            <Col span={10}>
                                                <div className="form-item">
                                                    <Text className="label required"><FormattedMessage id="checkout.firstName" defaultMessage="First name" /></Text>
                                                    <Input
                                                        value={holder.givenName}
                                                        onChange={(e) => handleHolderChange('givenName', e.target.value)}
                                                        className={errors.holder.givenName ? 'error-border' : ''}
                                                    />
                                                    {errors.holder.givenName && <Text className="activity-error-message">{errors.holder.givenName}</Text>}
                                                </div>
                                            </Col>
                                            <Col span={10}>
                                                <div className="form-item">
                                                    <Text className="label required"><FormattedMessage id="checkout.lastName" defaultMessage="Last name" /></Text>
                                                    <Input
                                                        value={holder.surName}
                                                        onChange={(e) => handleHolderChange('surName', e.target.value)}
                                                        className={errors.holder.surName ? 'error-border' : ''}
                                                    />
                                                    {errors.holder.surName && <Text className="activity-error-message">{errors.holder.surName}</Text>}
                                                </div>
                                            </Col>

                                            <Col span={24}>
                                                <div className="form-item">
                                                    <Text className="label required"><FormattedMessage id="checkout.emailAddress" defaultMessage="Email address" /></Text>
                                                    <Input
                                                        value={holder.email}
                                                        onChange={(e) => handleHolderChange('email', e.target.value)}
                                                        className={errors.holder.email ? 'error-border' : ''}
                                                    />
                                                    {errors.holder.email && <Text className="activity-error-message">{errors.holder.email}</Text>}
                                                </div>
                                            </Col>

                                            <Col span={8}>
                                                <div className="form-item">
                                                    <Text className="label"><FormattedMessage id="checkout.countryCode" defaultMessage="Country Code" /></Text>
                                                    <Select
                                                        value={holder.countryCode}
                                                        onChange={(val) => handleHolderChange('countryCode', val)}
                                                        style={{ width: '100%' }}
                                                        showSearch
                                                    >
                                                        {countriesCode.map(c => (
                                                            <Option key={c.isoCode} value={c.isoCode}>{c.name} ({c.dialCode})</Option>
                                                        ))}
                                                    </Select>
                                                </div>
                                            </Col>
                                            <Col span={16}>
                                                <div className="form-item">
                                                    <Text className="label required"><FormattedMessage id="checkout.phoneNumber" defaultMessage="Phone number" /></Text>
                                                    <Input
                                                        type="text"
                                                        pattern="[0-9]*"
                                                        maxLength={15}
                                                        value={phoneDisplay}
                                                        onChange={(e) => handleHolderChange('phone', e.target.value)}
                                                        className={errors.holder.phone ? 'error-border' : ''}
                                                    />
                                                    {errors.holder.phone && <Text className="activity-error-message">{errors.holder.phone}</Text>}
                                                </div>
                                            </Col>

                                            <Col span={24}>
                                                <div className="form-item">
                                                    <Text className="label required"><FormattedMessage id="checkout.address" defaultMessage="Address" /></Text>
                                                    <Input
                                                        value={holder.addressLine}
                                                        onChange={(e) => handleHolderChange('addressLine', e.target.value)}
                                                        className={errors.holder.addressLine ? 'error-border' : ''}
                                                    />
                                                    {errors.holder.addressLine && <Text className="activity-error-message">{errors.holder.addressLine}</Text>}
                                                </div>
                                            </Col>

                                            <Col span={12}>
                                                <div className="form-item">
                                                    <Text className="label required"><FormattedMessage id="checkout.city" defaultMessage="City" /></Text>
                                                    <Input
                                                        value={holder.city}
                                                        onChange={(e) => handleHolderChange('city', e.target.value)}
                                                        className={errors.holder.city ? 'error-border' : ''}
                                                    />
                                                    {errors.holder.city && <Text className="activity-error-message">{errors.holder.city}</Text>}
                                                </div>
                                            </Col>

                                            {holder.country === 'US' && (
                                                <Col span={12}>
                                                    <div className="form-item">
                                                        <Text className="label required"><FormattedMessage id="checkout.stateProvince" defaultMessage="State / Province" /></Text>
                                                        <Select
                                                            showSearch
                                                            placeholder={intl.formatMessage({ id: 'checkout.selectState', defaultMessage: 'Select State' })}
                                                            optionFilterProp="children"
                                                            value={holder.stateCode}
                                                            onChange={(val) => handleHolderChange('stateCode', val)}
                                                            className={errors.holder.stateCode ? 'error-border' : ''}
                                                            style={{ width: '100%' }}
                                                            filterOption={(input, option) =>
                                                                (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase()) ||
                                                                (option?.value as string).toLowerCase().includes(input.toLowerCase())
                                                            }
                                                        >
                                                            {US_STATES.map(state => (
                                                                <Option key={state.code} value={state.code}>{state.name}</Option>
                                                            ))}
                                                        </Select>
                                                        {errors.holder.stateCode && <Text className="activity-error-message">{errors.holder.stateCode}</Text>}
                                                    </div>
                                                </Col>
                                            )}

                                            <Col span={12}>
                                                <div className="form-item">
                                                    <Text className="label required"><FormattedMessage id="checkout.postalCode" defaultMessage="Postal Code" /></Text>
                                                    <Input
                                                        value={holder.postalCode}
                                                        onChange={(e) => handleHolderChange('postalCode', e.target.value)}
                                                        className={errors.holder.postalCode ? 'error-border' : ''}
                                                    />
                                                    {errors.holder.postalCode && <Text className="activity-error-message">{errors.holder.postalCode}</Text>}
                                                </div>
                                            </Col>

                                            <Col span={12}>
                                                <div className="form-item">
                                                    <Text className="label required"><FormattedMessage id="checkout.country" defaultMessage="Country" /></Text>
                                                    <Select
                                                        value={holder.country}
                                                        onChange={(val) => handleHolderChange('country', val)}
                                                        style={{ width: '100%' }}
                                                        showSearch
                                                    >
                                                        {countriesCode.map(c => (
                                                            <Option key={c.isoCode} value={c.isoCode}>{c.name}</Option>
                                                        ))}
                                                    </Select>
                                                </div>
                                            </Col>
                                        </Row>

                                        <div className="step-actions mt-4 text-start">
                                            <Button type="primary" size="large" onClick={handleNextStep} className="next-btn" disabled={!isStep1Valid}><FormattedMessage id="checkout.next" defaultMessage="Next" /></Button>
                                        </div>
                                    </div>
                                )}
                            </Card>

                            {/* Step 2: Guest Details */}
                            <Card className={`step-card ${currentStep === 2 ? 'active' : ''} ${currentStep < 2 ? 'disabled' : ''}`}>
                                <div className="step-header" onClick={() => currentStep >= 2 && setCurrentStep(2)}>
                                    <div className={`step-number ${currentStep > 2 ? 'completed' : ''}`}>
                                        {currentStep > 2 ? <CheckCircleFilled /> : 2}
                                    </div>
                                    <Title level={4} className="step-title"><FormattedMessage id="checkout.guestDetails" defaultMessage="Guest details" /></Title>
                                </div>
                                {currentStep === 2 && (
                                    <div className="step-content">
                                        <Text className="step-description"><FormattedMessage id="checkout.guestDescription" defaultMessage="Enter the names of the people participating in this activity." /></Text>

                                        {paxes.map((guest, idx) => {
                                            const guestTypeLabel = formatGuestTypeWithAge(guest.type, guest.age);
                                            return (
                                            <div key={idx} className="guest-form-block mb-4">
                                                <Title level={5} className="mb-3 text-start">
                                                    <FormattedMessage id="checkout.guestIndex" values={{ index: idx + 1 }} defaultMessage="Guest {index}" />
                                                    {guestTypeLabel != null && (
                                                        <> {' '}({guestTypeLabel})</>
                                                    )}
                                                    {idx === 0 && (
                                                        <> <FormattedMessage id="checkout.mainGuest" defaultMessage="(Main Guest)" /></>
                                                    )}
                                                </Title>
                                                <Row gutter={[16, 16]}>
                                                    <Col span={12}>
                                                        <div className="form-item">
                                                            <Text className="label required"><FormattedMessage id="checkout.firstName" defaultMessage="First name" /></Text>
                                                            <Input
                                                                value={guest.givenName}
                                                                onChange={(e) => handlePaxChange(idx, 'givenName', e.target.value)}
                                                                className={errors.paxes[idx]?.givenName ? 'error-border' : ''}
                                                            />
                                                            {errors.paxes[idx]?.givenName && <Text className="activity-error-message">{errors.paxes[idx]?.givenName}</Text>}
                                                        </div>
                                                    </Col>
                                                    <Col span={12}>
                                                        <div className="form-item">
                                                            <Text className="label required"><FormattedMessage id="checkout.lastName" defaultMessage="Last name" /></Text>
                                                            <Input
                                                                value={guest.surName}
                                                                onChange={(e) => handlePaxChange(idx, 'surName', e.target.value)}
                                                                className={errors.paxes[idx]?.surName ? 'error-border' : ''}
                                                            />
                                                            {errors.paxes[idx]?.surName && <Text className="activity-error-message">{errors.paxes[idx]?.surName}</Text>}
                                                        </div>
                                                    </Col>
                                                    {/*<Col span={12}>
                                                        <div className="form-item">
                                                            <Text className="label required"><FormattedMessage id="checkout.guestType" defaultMessage="Guest Type" /></Text>
                                                            <Select
                                                                value={guest.type}
                                                                onChange={(val) => handlePaxChange(idx, 'type', val)}
                                                                style={{ width: '100%' }}
                                                                className={errors.paxes[idx]?.type ? 'error-border' : ''}
                                                            >
                                                                <Option value="ADULT"><FormattedMessage id="adult" values={{ count: 1 }} defaultMessage="Adult" /></Option>
                                                                <Option value="CHILD"><FormattedMessage id="child" values={{ count: 1 }} defaultMessage="Child" /></Option>
                                                                <Option value="INFANT"><FormattedMessage id="checkout.infant" defaultMessage="Infant" /></Option>
                                                            </Select>
                                                            {errors.paxes[idx]?.type && <Text className="activity-error-message">{errors.paxes[idx]?.type}</Text>}
                                                        </div>
                                                    </Col>*/}
                                                </Row>
                                                {/* Per traveler dynamic questions */}
                                                <GuestQuestions
                                                    idx={idx}
                                                    guest={guest}
                                                    detailLabel={detailLabel}
                                                    travelerAnswers={travelerAnswers}
                                                    activityDateIso={activityDateIsoForAge}
                                                    ageBands={detailLabel.ageBands}
                                                    questionMetaList={questionData?.questions}
                                                />
                                            </div>
                                            );
                                        })}

                                        {detailLabel.languageGuides && detailLabel.languageGuides.length > 0 && (
                                            <div className="form-item mb-4 text-start" style={{ maxWidth: '400px' }}>
                                                <Text className="label required"><FormattedMessage id="checkout.tourLanguage" defaultMessage="Tour language" /></Text>
                                                <Select
                                                    value={selectedLanguageGuide ? `${selectedLanguageGuide.language} - ${selectedLanguageGuide.type}` : undefined}
                                                    onChange={(val) => {
                                                        const [lang, type] = val.split(' - ');
                                                        setSelectedLanguageGuide({ language: lang, type: type });
                                                    }}
                                                    style={{ width: '100%' }}
                                                    placeholder={intl.formatMessage({ id: 'checkout.selectTourLanguage', defaultMessage: 'Select Tour Language' })}
                                                >
                                                    {detailLabel.languageGuides.map((guide, idx) => (
                                                        <Option key={idx} value={`${guide.language} - ${guide.type}`}>
                                                            {getLanguageName(guide.language)} - {guide.type.charAt(0).toUpperCase()}{guide.type.slice(1).toLowerCase()}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </div>
                                        )}

                                        <BookingQuestions
                                            detailLabel={detailLabel}
                                            bookingAnswers={bookingAnswers}
                                            travelerAnswers={travelerAnswers}
                                            conditionalData={conditionalData}
                                            questionData={questionData}
                                        />

                                        <div className="step-actions mt-4 text-start">
                                            <Button type="primary" size="large" onClick={handleNextStep} className="next-btn" disabled={!isStep2Valid}><FormattedMessage id="checkout.next" defaultMessage="Next" /></Button>
                                            <Button type="link" size="large" className='back-button' onClick={() => setCurrentStep(1)}><FormattedMessage id="checkout.back" defaultMessage="Back" /></Button>
                                        </div>
                                    </div>
                                )}
                            </Card>

                            {/* Step 3: Payment details */}
                            <Card className={`step-card ${currentStep === 3 ? 'active' : ''} ${currentStep < 3 ? 'disabled' : ''}`}>
                                <div className="step-header" onClick={() => currentStep === 3 && setCurrentStep(3)}>
                                    <div className="step-number">3</div>
                                    <Title level={4} className="step-title"><FormattedMessage id="checkout.paymentDetails" defaultMessage="Payment details" /></Title>
                                </div>
                                {currentStep === 3 && (
                                    <div className="step-content">
                                        <Text className="step-description"><FormattedMessage id="checkout.encryptedTransactions" defaultMessage="All transactions are secure and encrypted." /></Text>

                                          {/* Skeleton while Stripe wallet probe is running */}
                                          {stripeRegionOk && !suppressStripePaymentUI && !expressWalletProbeComplete && (
                                            <Card className="activity-payment-methods-select__card mb-3" style={{ marginTop: 20 }}>
                                              <Skeleton active paragraph={{ rows: 3 }} title={{ width: '40%' }} />
                                              <Skeleton.Button active block style={{ marginTop: 20, height: 40 }} />
                                            </Card>
                                          )}

                                          {showExpressCheckoutFlow && (<PaymentMethodsSelect setPaymentSelected={setPaymentSelected} multiplePayments={multipayment} scrollToPaymentSelect={scrollToPaymentSelect}
                                            scrollToPaymentSelectDisabled={() => setScrollToPaymentSelect(false)} />)}

                                        <div className="payment-method-wrapper mt-4">
                                            {isCardPayment && (
                                            <PaymentForm
                                                data={paymentFormSyncProps}
                                                onChange={(data: PaymentFormData) => {
                                                    const digits = (data.cardNumber || '').replace(/\D/g, '');
                                                    const fromPan = digits ? detectCardType(digits) : undefined;
                                                    const effectiveCardType =
                                                        data.cardType !== undefined &&
                                                        data.cardType !== null &&
                                                        String(data.cardType).trim() !== ''
                                                            ? String(data.cardType).trim()
                                                            : fromPan != null
                                                              ? String(fromPan)
                                                              : '';
                                                    setCard((prev: ICardPayload) => ({
                                                        ...prev,
                                                        cardNumber: data.cardNumber,
                                                        holderName: data.nameOnCard,
                                                        expireDate: `${data.expiryMonth}${data.expiryYear}`,
                                                        cvv: data.securityCode,
                                                        cardType: effectiveCardType,
                                                    }));
                                                }}
                                            />
                                            )}
                                        </div>

                                        {/* Non-force-full: acknowledgements render inline above the button.
                                            Force-full: passed into the force-full component's `footer` (below). */}
                                        {!suppressStripePaymentUI && acknowledgementsSection}

                                        <div className="step-actions mt-4 text-start">
                                            {suppressStripePaymentUI && (
                                                <ActivityForceFullStripeCheckout
                                                    amount={Math.round(detailLabel.price * 100)}
                                                    currency={detailLabel.currency || account?.currency || 'USD'}
                                                    // Force-full: the card lives in Stripe's PaymentElement (not React `card`
                                                    // state), so don't gate on `isStep3Valid` (card fields). Steps 1 & 2 are
                                                    // already enforced by the wizard; only the acknowledgements remain. Mirrors
                                                    // the hotels (rsi) force-full gate, which never checks the card form.
                                                    isDisabled={!termsAccepted || !cancellationAccepted || (usingClientCashNonRefundable && !clientCashNotRefundableAccepted)}
                                                    isLoading={isBookingLoading}
                                                    enabledMethods={paymentButtonKeys}
                                                    footer={acknowledgementsSection}
                                                    onCompleteBooking={handleCompleteBooking}
                                                    onBack={() => setCurrentStep(2)}
                                                />
                                            )}

                                            {!suppressStripePaymentUI && shouldMountStripeExpressElements && (
                                                <div style={{ display: (showExpressCheckoutFlow && !isEmpty(paymentSelected) && paymentSelected !== 'card') ? 'block' : 'none' }}>
                                                    <Elements
                                                        stripe={stripePromise}
                                                        options={{
                                                            mode: 'payment',
                                                            // Must match the BFF PaymentIntent (manual capture). Capture happens after the
                                                            // provider booking succeeds; on failure the authorization is voided (no refund).
                                                            captureMethod: 'manual',
                                                            amount: Math.round(detailLabel.price * 100),
                                                            currency: (detailLabel.currency || account?.currency || 'USD').toLowerCase(),
                                                        }}
                                                    >
                                                        <ActivityExpressCheckoutButtons
                                                            paymentSelected={expressPaymentSelected}
                                                            // Wallet (express) payment collects the card in Stripe's own sheet, NOT the React
                                                            // card form — so don't gate on `isStep3Valid` (which requires the card fields), or
                                                            // the wallet button stays disabled forever. Only acknowledgements + wallet validity
                                                            // apply (steps 1 & 2 are already enforced by the wizard). Mirrors front-rsi.
                                                            isDisabled={!termsAccepted || !cancellationAccepted || !isReviewClientCashValid || isBookingLoading || (usingClientCashNonRefundable && !clientCashNotRefundableAccepted)}
                                                            isLoading={isBookingLoading}
                                                            enabledMethods={paymentButtonKeys}
                                                            onExpressWalletAvailabilityResolved={(hasWallets, availableMethods) => {
                                                                setExpressWalletProbeComplete(true);
                                                                setHasExpressWalletMethods(hasWallets);
                                                                if (availableMethods && hasWallets) {
                                                                    setMultipayement({
                                                                        amazonPay: !!availableMethods.amazonPay,
                                                                        applePay: !!availableMethods.applePay,
                                                                        googlePay: !!availableMethods.googlePay,
                                                                        link: !!availableMethods.link,
                                                                        paypal: !!availableMethods.paypal,
                                                                    });
                                                                }
                                                            }}
                                                            onCompleteBooking={handleCompleteBooking}
                                                        />
                                                    </Elements>
                                                </div>
                                            )}

                                            {!suppressStripePaymentUI && isCardPayment && (
                                                <Button
                                                    onClick={() => handleCompleteBooking()}
                                                    disabled={!isStep3Valid || isBookingLoading || !isReviewClientCashValid || !termsAccepted || !cancellationAccepted || (usingClientCashNonRefundable && !clientCashNotRefundableAccepted)}
                                                    loading={isBookingLoading}
                                                    className="next-btn "
                                                >
                                                    <FormattedMessage id="activity.checkout.complete_booking.button" defaultMessage="Complete Booking" />
                                                </Button>
                                            )}

                                            {!suppressStripePaymentUI && (
                                                <Button type="link" onClick={() => setCurrentStep(2)} className=" back-button"><FormattedMessage id="checkout.backToGuestDetails" defaultMessage="Back to guest details" /></Button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </Card>
                        </div>
                    </Col>

                    <Col className='mobile-order1' xs={24} lg={10} xl={8}>
                        <BookingSummary
                            detailLabel={detailLabel}
                            dates={dates}
                            roomsSelected={roomsSelected}
                            onWalletValidityChange={setIsReviewClientCashValid}
                        />
                    </Col>
                </Row>

                {/* The section lives on the confirmation page (BookingConfirmation renders it
                    there). `CROSS_SELL_AT_CHECKOUT` mirrors it here so the recommendations can
                    be exercised without completing a booking; off in every env. */}
                {CROSS_SELL_AT_CHECKOUT && (
                    <Row gutter={24}>
                        {/* Same Col as the checkout's main column above (xs 24 / lg 14 / xl 16)
                            so the section lines up with it instead of spanning the full width. */}
                        <Col xs={24} lg={14} xl={16}>
                            <CrossSellSection
                                origin="activity"
                                propertyId={detailLabel.activityCode}
                                checkIn={dates?.startDate}
                                currency={selectedCurrency || detailLabel.currency || 'USD'}
                            />
                        </Col>
                    </Row>
                )}

                <OfferExpiredModal
                    visible={showExpiredModal}
                    accountName={accountName}
                    activityCode={detailLabel.activityCode}
                />

                <Modal
                    visible={showNotRefundableModal}
                    onOk={() => setShowNotRefundableModal(false)}
                    onCancel={() => setShowNotRefundableModal(false)}
                    footer={null}
                    centered
                >
                    <FormattedMessage
                        id="wallet.client.not_refundable.modal.acknowledge"
                        values={{ clientCashName: account?.walletClientCashName }}
                    />
                </Modal>
            </div>
        </div>
    );
};
